import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  CHANNEL_NAMES,
  connectionLabel,
  errorCode,
  magicLinkKey,
  useConfirmMagicLink,
  useConnections,
  useMagicLink,
  type MagicLink,
  type MessagingChannel,
} from "./api";
import { ConnectionLine } from "./ConnectionLine";
import { LinkCodeBlock } from "./LinkCodeBlock";
import { ARIA_DISABLED_LOOK, FLAT_ON_PHONE, TAP_ON_PHONE } from "./classes";
import { Spinner } from "./Spinner";

const TOKEN_KEY = "agent.linkToken";

function storedToken(): string {
  try {
    return sessionStorage.getItem(TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

/** Drop a spent token from the tab's storage (unless another link has replaced it since). */
function forgetToken(token: string) {
  try {
    if (sessionStorage.getItem(TOKEN_KEY) === token) sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    // Nothing stored, nothing to drop.
  }
}

/**
 * The magic-link token from the URL fragment (never sent to a server), kept for this tab and then
 * dropped from the address bar and history; a reload reads it back from the tab's storage. A new
 * fragment (another link opened in the same tab) replaces it.
 */
function useLinkToken(): string {
  const location = useLocation();
  const fromUrl = location.hash.replace(/^#/, "");
  const [token, setToken] = useState(() => fromUrl || storedToken());
  useEffect(() => {
    if (!fromUrl) return;
    try {
      sessionStorage.setItem(TOKEN_KEY, fromUrl);
    } catch {
      // Without storage a reload just offers a fresh code.
    }
    setToken(fromUrl);
    window.history.replaceState(window.history.state, "", location.pathname + location.search);
  }, [fromUrl, location.pathname, location.search]);
  return token;
}

/** /link/:channel#<token>: confirm the number that asked to link (the link a chat was sent). */
export default function LinkConfirmPage() {
  const token = useLinkToken();
  // A new token starts over: nothing from the previous link's peek or confirm carries across.
  return <LinkConfirm key={token} token={token} />;
}

function LinkConfirm({ token }: { token: string }) {
  const { channel: rawChannel = "" } = useParams();
  const link = useMagicLink(token);
  const connections = useConnections();
  const confirm = useConfirmMagicLink(token);
  // The server's channel for this token wins: the path is only a hint (a link can be rewritten).
  const pathChannel = (rawChannel in CHANNEL_NAMES ? rawChannel : "whatsapp") as MessagingChannel;
  const channel =
    confirm.data?.channel ?? (link.data?.status === "open" ? link.data.channel : pathChannel);
  const name = CHANNEL_NAMES[channel];
  const spent = confirm.isSuccess || link.data?.status === "closed";
  const queryClient = useQueryClient();
  const confirmed = confirm.isSuccess;
  useEffect(() => {
    if (!spent) return;
    forgetToken(token);
    // A confirmed link is spent: a later visit in this session must not paint the cached open
    // peek. Written only once the confirm has succeeded, never while it is pending.
    if (confirmed) {
      queryClient.setQueryData<MagicLink>(magicLinkKey(token), { status: "closed" });
    }
  }, [spent, confirmed, token, queryClient]);

  if (link.isLoading || connections.isLoading) {
    return (
      <Page title={`Link ${name}`}>
        <Spinner label="Loading link" className="self-center" />
      </Page>
    );
  }

  if (confirm.isSuccess) {
    return (
      <Page title={`${name} linked`}>
        <ConnectionLine channel={channel} label={connectionLabel(confirm.data)} linked />
        <Button asChild className={cn("w-full", TAP_ON_PHONE)}>
          <Link to="/profile">Done</Link>
        </Button>
      </Page>
    );
  }

  const open = link.data?.status === "open" ? link.data : null;
  const openProfile = (
    <Button asChild variant="outline" className={TAP_ON_PHONE}>
      <Link to="/profile">Open profile</Link>
    </Button>
  );
  // Refused at the limit, the link is still open: the page shows what the peek says now (which
  // follows a number freed in another tab), not a fresh code.
  const refusedAtCap = errorCode(confirm.error) === "at_cap";
  // A link that was used, ran out or failed to confirm offers a fresh code instead.
  if (!open || (confirm.isError && !refusedAtCap)) {
    // Only a channel that is available here offers a fresh code.
    if (!connections.data?.channels.includes(channel)) {
      return <Page title={`Link ${name}`}>{openProfile}</Page>;
    }
    return (
      <Page title={`Scan code to link ${name}`}>
        <LinkCodeBlock channel={channel} />
      </Page>
    );
  }

  const number = connectionLabel(open);
  if (open.limit > 1 && open.at_cap) {
    // A channel that takes several numbers is full: one has to go before this one can join.
    return (
      <Page title={`Link ${name}`}>
        {open.linked.map((row) => (
          <ConnectionLine key={row.id} channel={channel} label={connectionLabel(row)} linked />
        ))}
        <p className="text-sm text-muted-foreground">
          {open.linked.length} numbers linked. Disconnect one to add{" "}
          {/* A phone number stays on one line; a long display name may wrap. */}
          <span className={open.phone ? "whitespace-nowrap" : undefined}>{number}</span>.
        </p>
        {openProfile}
      </Page>
    );
  }

  // A channel that takes several numbers adds this one beside the others; one that takes a single
  // link switches it to this number.
  const adding = open.limit > 1;
  const current = adding ? undefined : open.linked[0];
  const switching = current !== undefined && connectionLabel(current) !== number;
  const confirmButton = (
    <Button
      className={cn(TAP_ON_PHONE, ARIA_DISABLED_LOOK, switching && "flex-1")}
      aria-disabled={confirm.isPending}
      onClick={() => {
        if (!confirm.isPending) confirm.mutate();
      }}
    >
      {switching ? `Switch to ${number}` : adding ? `Add ${number} to your account` : "Connect"}
    </Button>
  );

  // Already linked to another number: the current one stays unless the user switches.
  if (switching) {
    return (
      <Page title="Your account is already linked">
        <ConnectionLine channel={channel} label={connectionLabel(current)} linked />
        <div className="flex flex-wrap gap-2">
          {confirmButton}
          <Button asChild variant="outline" className={TAP_ON_PHONE}>
            <Link to="/profile">Keep current</Link>
          </Button>
        </div>
      </Page>
    );
  }

  return (
    <Page title={`Link ${name}`}>
      <ConnectionLine channel={channel} label={number} />
      {confirmButton}
    </Page>
  );
}

function Page({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-md py-8">
      <Card className={cn("shadow-none", FLAT_ON_PHONE)}>
        <CardHeader>
          <CardTitle role="heading" aria-level={1}>
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">{children}</CardContent>
      </Card>
    </div>
  );
}
